import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './DisclaimerModal.css';

const STORAGE_KEY_PERMANENT = 'theychat-disclaimer-dismissed';
const STORAGE_KEY_SESSION = 'theychat-disclaimer-shown';

interface DisclaimerItemConfig {
  labelKey: string;
  textKey: string;
  defaultLabel: string;
  isHighlighted?: boolean;
}

const DISCLAIMER_ITEMS: DisclaimerItemConfig[] = [
  {
    labelKey: 'aiDataSharingLabel',
    textKey: 'aiDataSharingText',
    defaultLabel: 'AI & Third-Party Data Sharing:',
    isHighlighted: true,
  },
  {
    labelKey: 'aiProtectionLabel',
    textKey: 'aiProtectionText',
    defaultLabel: 'Data Protection & Equal Privacy:',
    isHighlighted: true,
  },
  {
    labelKey: 'representativeLabel',
    textKey: 'representativeText',
    defaultLabel: 'Representative Nature:',
  },
  {
    labelKey: 'notRulingLabel',
    textKey: 'notRulingText',
    defaultLabel: 'Not a Religious Ruling:',
  },
  {
    labelKey: 'hallucinationLabel',
    textKey: 'hallucinationText',
    defaultLabel: 'Margin of Error (Hallucination):',
  },
  {
    labelKey: 'chronologicalLabel',
    textKey: 'chronologicalText',
    defaultLabel: 'Chronological Inconsistencies:',
  },
  {
    labelKey: 'modernLabel',
    textKey: 'modernText',
    defaultLabel: 'Modern Concepts:',
  },
  {
    labelKey: 'styleLabel',
    textKey: 'styleText',
    defaultLabel: 'Style Limitations:',
  },
  {
    labelKey: 'sessionBillingLabel',
    textKey: 'sessionBillingText',
    defaultLabel: 'Session Minutes & Credits:',
  },
  {
    labelKey: 'sponsorshipLabel',
    textKey: 'sponsorshipText',
    defaultLabel: 'Sponsorship:',
  },
  {
    labelKey: 'disclaimerLabel',
    textKey: 'disclaimerText',
    defaultLabel: 'Disclaimer:',
  },
];

const DisclaimerModal: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Permanently dismissed — never show
    if (localStorage.getItem(STORAGE_KEY_PERMANENT)) return;
    // Already shown this session — don't show again until next visit
    if (sessionStorage.getItem(STORAGE_KEY_SESSION)) return;

    setIsOpen(true);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAccept = () => {
    // Permanently dismiss — user has read and accepted
    localStorage.setItem(STORAGE_KEY_PERMANENT, 'true');
    sessionStorage.setItem(STORAGE_KEY_SESSION, 'true');
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="disclaimer-modal-overlay">
      <div
        className="disclaimer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-modal-title"
      >
        {/* Shield Icon */}
        <div className="disclaimer-modal-icon">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1a1a1a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>

        {/* Title */}
        <h2 id="disclaimer-modal-title" className="disclaimer-modal-title">
          {t('disclaimer.title', 'Disclaimer')}
        </h2>

        {/* Scrollable Container for items */}
        <div className="disclaimer-modal-scroll">
          {/* Intro */}
          <p className="disclaimer-modal-intro">
            {t(
              'disclaimer.intro',
              'This platform is an educational and reflective bridge designed to help you learn about historical companions through modern AI. Before starting, please review our third-party AI data sharing disclosure and technical limitations:',
            )}
          </p>

          {/* Content - Bullet points */}
          <div className="disclaimer-modal-content">
            {DISCLAIMER_ITEMS.map((item) => (
              <div
                key={item.labelKey}
                className={`disclaimer-item ${item.isHighlighted ? 'disclaimer-item-highlighted' : ''}`}
              >
                <span
                  className={`disclaimer-item-label ${item.isHighlighted ? 'disclaimer-item-label-highlighted' : ''}`}
                >
                  {t(`disclaimer.${item.labelKey}`, item.defaultLabel)}
                </span>
                <span className="disclaimer-item-text"> {t(`disclaimer.${item.textKey}`)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Links (Privacy Policy & Terms) */}
        <div className="disclaimer-modal-links">
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="disclaimer-modal-link"
          >
            {t('privacy.title', 'Privacy Policy')}
          </a>
          <span className="disclaimer-modal-link-divider">•</span>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="disclaimer-modal-link"
          >
            {t('terms.title', 'Terms of Use')}
          </a>
        </div>

        {/* Checkbox */}
        <label className="disclaimer-checkbox-label">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="disclaimer-checkbox"
          />
          <span className="disclaimer-checkbox-custom" />
          <span className="disclaimer-checkbox-text">
            {t(
              'disclaimer.checkboxText',
              'I acknowledge the technical limitations and explicitly consent to sharing my conversation inputs with third-party AI services (Google Gemini, HeyGen) as described in the Privacy Policy.',
            )}
          </span>
        </label>

        {/* Accept Button */}
        <div className="disclaimer-modal-actions">
          <button
            type="button"
            className="disclaimer-modal-btn-primary"
            onClick={handleAccept}
            disabled={!accepted}
          >
            {t('disclaimer.accept', 'I CONSENT & ACCEPT')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DisclaimerModal;
